import { useEffect, useRef, useState } from 'react';
import { formatDate, getMonthName } from '../../../lib/utils';
import { getCalendarDates } from '../../../lib/date';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import usePreference from '../../../hook/usePreference';
import request from '../../../lib/request';
import CreateLog from './create';
import TrackerView from './view';

const weeks = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const Calendar = ({ user, currentProgram }) => {
  const { preference } = usePreference();

  const createDialogRef = useRef(null);
  const trackerViewRef = useRef(null);

  const [selectedDate, setSelectedDate] = useState('');
  const [dailyTracker, setDailyTracker] = useState({});
  const [workoutTrackers, setWorkoutTrackers] = useState([]);
  const [openFromView, setOpenFromView] = useState(false);

  const [calendar, setCalendar] = useState(null);
  const [currentYear, setCurrentYear] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(null);

  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    setCurrentYear(year);
    setCurrentMonth(month);
    setCalendar(getCalendarDates(year, month));
    getWorkoutTracker(year, month);
  }, []);

  useEffect(() => {
    if (currentYear && currentMonth) {
      getWorkoutTracker(currentYear, currentMonth);
    }
  }, [currentMonth]);

  const getWorkoutTracker = async (year, month) => {
    const startDate = `${year}-${month.toString().padStart(2, '0')}-01`;
    const endDate = `${year}-${month.toString().padStart(2, '0')}-${new Date(
      year,
      month,
      0
    ).getDate()}`;

    try {
      const { result } = await request.get(
        `/workout?startDate=${startDate}&endDate=${endDate}`
      );

      const grouped = {};

      result.forEach((tracker) => {
        let year, month, date;

        if (tracker.workoutAt) {
          [year, month, date] = formatDate(new Date(tracker.workoutAt)).split(
            '-'
          );
        } else {
          [year, month, date] = tracker.logDate.split('-');
        }

        if (!grouped[year]) {
          grouped[year] = {};
        }
        if (!grouped[year][month]) {
          grouped[year][month] = {};
        }
        if (!grouped[year][month][date]) {
          grouped[year][month][date] = [];
        }
        grouped[year][month][date].push(tracker);
      });

      setDailyTracker(grouped);
    } catch (err) {
      console.log(err);
    }
  };

  const getWorkoutTrackerLog = async (date) => {
    try {
      const startDate = new Date(`${date}T00:00:00`).toISOString();
      const endDate = new Date(`${date}T23:59:59.999`).toISOString();

      const { result } = await request.get(
        `/workout/log?date=${date}&startDate=${startDate}&endDate=${endDate}`
      );
      setWorkoutTrackers(result);
    } catch (err) {
      console.log(err);
    }
  };

  const refreshWorkoutTracker = async () => {
    const [year, month] = selectedDate.split('-');
    await getWorkoutTracker(year, month);
  };

  const refreshWorkoutTrackerLog = async () => {
    await getWorkoutTrackerLog(selectedDate);
  };

  const openCreateDialog = () => {
    if (createDialogRef.current) {
      createDialogRef.current.showModal();
    }
  };

  const closeCreateDialog = () => {
    if (createDialogRef.current) {
      createDialogRef.current.close();
    }
  };

  const openTrackerViewDialog = () => {
    if (trackerViewRef.current) {
      trackerViewRef.current.showModal();
    }
  };

  const closeTrackerViewDialog = () => {
    if (trackerViewRef.current) {
      trackerViewRef.current.close();
      setSelectedDate('');
    }
  };

  const handlePrevMonth = () => {
    if (currentMonth > 1) {
      setCurrentMonth(currentMonth - 1);
      setCalendar(getCalendarDates(currentYear, currentMonth - 1));
    }
    if (currentMonth === 1) {
      setCurrentYear(currentYear - 1);
      setCurrentMonth(12);
      setCalendar(getCalendarDates(currentYear - 1, 12));
    }
  };

  const handleNextMonth = () => {
    if (currentMonth < 12) {
      setCurrentMonth(currentMonth + 1);
      setCalendar(getCalendarDates(currentYear, currentMonth + 1));
    }
    if (currentMonth === 12) {
      setCurrentYear(currentYear + 1);
      setCurrentMonth(1);
      setCalendar(getCalendarDates(currentYear + 1, 1));
    }
  };

  const handleSelectDate = (value) => () => {
    setSelectedDate(value);
    const [year, month, date] = value.split('-');
    if (dailyTracker[year]?.[month]?.[date]) {
      getWorkoutTrackerLog(value);
      return openTrackerViewDialog();
    }
    openCreateDialog();
  };

  return (
    calendar && (
      <div>
        {Object.entries(calendar).map(([year, months]) => (
          <div key={year}>
            {Object.entries(months).map(([month, dates]) => (
              <div
                key={month}
                className='bg-secondary rounded-2xl p-4'
              >
                <div className='text-white mb-4 max-w-sm flex flex-col gap-2 mx-auto'>
                  <div className='font-bold'>
                    <div className='text-xs'>{year}</div>
                  </div>
                  <div className='flex items-center justify-between gap-2'>
                    <button
                      className='text-white'
                      onClick={handlePrevMonth}
                    >
                      <ChevronLeftIcon className='size-5' />
                    </button>
                    <div className='font-bold text-lg'>
                      {getMonthName(month)}
                    </div>
                    <button
                      className='text-white'
                      onClick={handleNextMonth}
                    >
                      <ChevronRightIcon className='size-5' />
                    </button>
                  </div>
                </div>
                <div className='grid grid-cols-7 text-xs font-medium mb-2'>
                  {weeks.map((week, i) => (
                    <div
                      key={i}
                      className='flex justify-center items-center text-gray-500'
                    >
                      {week}
                    </div>
                  ))}
                </div>
                <div className='grid grid-cols-7'>
                  {dates.map((date, i) => (
                    <div
                      key={i}
                      className='mb-2'
                      onClick={
                        date && handleSelectDate(`${year}-${month}-${date}`)
                      }
                    >
                      <div
                        className={`
                          relative h-10 flex justify-center items-center transition-all
                          ${date ? 'cursor-pointer' : ''}
                          ${
                            user.programRegistered &&
                            new Date(currentProgram.startDate) <=
                              new Date(`${year}-${month}-${date}`) &&
                            new Date(currentProgram.endDate) >=
                              new Date(`${year}-${month}-${date}`)
                              ? 'bg-accent/40 text-white'
                              : ''
                          }
                          ${
                            user.programRegistered &&
                            formatDate(new Date(currentProgram.startDate)) ===
                              `${year}-${month}-${date}`
                              ? 'rounded-l-full'
                              : formatDate(new Date(currentProgram.endDate)) ===
                                `${year}-${month}-${date}`
                              ? 'rounded-r-full'
                              : ''
                          }
                          `}
                      >
                        <span
                          className={`text-sm size-8 rounded-lg flex justify-center items-center ${
                            selectedDate === `${year}-${month}-${date}`
                              ? 'bg-white text-neutral-900'
                              : `${year}-${month}-${date}` ===
                                formatDate(new Date())
                              ? 'ring-1 ring-neutral-700 bg-neutral-800 text-white'
                              : dailyTracker[year]?.[month]?.[date]?.length > 0
                              ? 'bg-tertiary text-white font-medium shadow-lg'
                              : 'text-gray-300'
                          }`}
                        >
                          {date?.[0] === '0' ? date[1] : date}
                        </span>
                        {dailyTracker[year]?.[month]?.[date]?.length > 0 && (
                          <div className='absolute -bottom-[5px] left-1/2 -translate-x-1/2'>
                            <div className='flex gap-0.5'>
                              {[
                                ...Array(
                                  Math.min(
                                    3,
                                    dailyTracker[year][month][date].length
                                  )
                                ),
                              ].map((_, i) => (
                                <div
                                  key={i}
                                  className='w-1 h-1 bg-white rounded-full'
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
        <TrackerView
          dialogRef={trackerViewRef}
          closeDialog={closeTrackerViewDialog}
          selectedDate={selectedDate}
          openCreateDialog={() => {
            setOpenFromView(true);
            openCreateDialog();
          }}
          workoutTrackers={workoutTrackers}
          refreshWorkoutTracker={refreshWorkoutTracker}
          refreshWorkoutTrackerLog={refreshWorkoutTrackerLog}
          preference={preference}
          user={user}
        />
        <CreateLog
          dialogRef={createDialogRef}
          closeDialog={() => {
            setOpenFromView(false);
            closeCreateDialog();
          }}
          refreshWorkoutTracker={
            openFromView ? refreshWorkoutTrackerLog : refreshWorkoutTracker
          }
          selectedDate={selectedDate}
          preference={preference}
          user={user}
        />
      </div>
    )
  );
};

export default Calendar;
